export type AggregateSummary = {
  value: number | null
  contributors: number
  total: number
  contributingPropertyIds: number[]
}

export type ComponentAggregateSummary = AggregateSummary & {
  componentContributors: Record<string, number>
}

export type EventMetricValues = Record<number, Record<string, Record<number, number>>>

function isFiniteNumber(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

export function formatPropertyCoverage(available: number, total: number, label = 'selected properties') {
  return `${available} of ${total} ${label}`
}

export function sumAggregateValues(
  propertyIds: number[],
  getValue: (propertyId: number) => number | null | undefined,
): AggregateSummary {
  let totalValue = 0
  const contributingPropertyIds: number[] = []

  propertyIds.forEach(propertyId => {
    const value = getValue(propertyId)
    if (!isFiniteNumber(value)) return
    totalValue += value
    contributingPropertyIds.push(propertyId)
  })

  return {
    value: contributingPropertyIds.length ? totalValue : null,
    contributors: contributingPropertyIds.length,
    total: propertyIds.length,
    contributingPropertyIds,
  }
}

export function meanAggregateValues(
  propertyIds: number[],
  getValue: (propertyId: number) => number | null | undefined,
): AggregateSummary {
  const sumSummary = sumAggregateValues(propertyIds, getValue)
  return {
    ...sumSummary,
    value: sumSummary.contributors ? sumSummary.value! / sumSummary.contributors : null,
  }
}

export function sumAvailableComponentValues(
  propertyIds: number[],
  componentGetters: Record<string, (propertyId: number) => number | null | undefined>,
): ComponentAggregateSummary {
  let totalValue = 0
  let found = false
  const contributingPropertyIds = new Set<number>()
  const componentContributors: Record<string, number> = {}

  Object.keys(componentGetters).forEach(componentKey => {
    componentContributors[componentKey] = 0
  })

  propertyIds.forEach(propertyId => {
    Object.entries(componentGetters).forEach(([componentKey, getValue]) => {
      const value = getValue(propertyId)
      if (!isFiniteNumber(value)) return
      totalValue += value
      found = true
      contributingPropertyIds.add(propertyId)
      componentContributors[componentKey] += 1
    })
  })

  return {
    value: found ? totalValue : null,
    contributors: contributingPropertyIds.size,
    total: propertyIds.length,
    contributingPropertyIds: Array.from(contributingPropertyIds),
    componentContributors,
  }
}

function hasCompleteEventSeriesForYear(
  eventValues: Record<string, Record<number, number>> | undefined,
  year: number,
  requiredReturnPeriods?: string[],
) {
  if (!eventValues) return false
  if (!requiredReturnPeriods?.length) return true

  return requiredReturnPeriods.every(returnPeriod => isFiniteNumber(eventValues[returnPeriod]?.[year]))
}

export function aggregateEventMetricValues(
  propertyIds: number[],
  metricValues: EventMetricValues,
  returnPeriod: string,
  year: number,
  aggregation: 'sum' | 'mean',
  requiredReturnPeriods?: string[],
): AggregateSummary {
  let totalValue = 0
  const contributingPropertyIds: number[] = []

  propertyIds.forEach(propertyId => {
    const eventValues = metricValues[propertyId]
    if (!hasCompleteEventSeriesForYear(eventValues, year, requiredReturnPeriods)) return

    const value = eventValues?.[returnPeriod]?.[year]
    if (!isFiniteNumber(value)) return

    totalValue += value
    contributingPropertyIds.push(propertyId)
  })

  const contributors = contributingPropertyIds.length

  return {
    value:
      contributors > 0
        ? aggregation === 'mean'
          ? totalValue / contributors
          : totalValue
        : null,
    contributors,
    total: propertyIds.length,
    contributingPropertyIds,
  }
}
